/**
 * Course Data Fetcher Utility
 * Provides functions for fetching course, session group, and session details
 */

/**
 * Fetches course data by ID
 * @param {string} courseId - The course ID
 * @param {Object} headers - Optional headers for authenticated requests
 * @returns {Promise<Object>} Course data
 */
export const fetchCourseData = async (courseId, headers = {}) => {
  if (!courseId) {
    throw new Error('Course ID is required');
  }

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/courses/${courseId}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch course: ${response.status}`);
  }

  return response.json();
};

/**
 * Fetches session groups for a course
 * @param {string} courseId - The course ID
 * @param {Object} headers - Optional headers for authenticated requests
 * @returns {Promise<Array>} Array of session groups
 */
export const fetchSessionGroups = async (courseId, headers = {}) => {
  if (!courseId) {
    throw new Error('Course ID is required');
  }

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/courses/session-groups?courseId=${courseId}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch session groups: ${response.status}`);
  }

  const data = await response.json();
  return data.sessionGroups || [];
};

/**
 * Fetches detailed session group data including sessions
 * @param {string} sessionGroupId - The session group ID
 * @param {Object} headers - Optional headers for authenticated requests
 * @returns {Promise<Object>} Session group with sessions
 */
export const fetchSessionGroupDetails = async (sessionGroupId, headers = {}) => {
  if (!sessionGroupId) {
    throw new Error('Session group ID is required');
  }

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/courses/session-groups/${sessionGroupId}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch session group details: ${response.status}`);
  }

  return response.json();
};

/**
 * Fetches all session groups with full details for a course
 * @param {string} courseId - The course ID
 * @param {Object} headers - Optional headers for authenticated requests
 * @returns {Promise<Array>} Array of session groups with full details
 */
export const fetchAllSessionGroupsWithDetails = async (courseId, headers = {}) => {
  const sessionGroups = await fetchSessionGroups(courseId, headers);
  
  // Fetch details for each session group in parallel
  const detailPromises = sessionGroups.map(group => 
    fetchSessionGroupDetails(group.id, headers).catch(() => null)
  );
  
  const allGroupDetails = await Promise.all(detailPromises);
  return allGroupDetails.filter(Boolean);
};

/**
 * Categorizes session groups into past, current, and future
 * @param {Array} sessionGroups - Array of session groups with details
 * @returns {Object} Object with past, current, and future arrays
 */
export const categorizeSessionGroups = (sessionGroups) => {
  const now = new Date();
  const sixtyMinutesAgo = new Date(now.getTime() - 60 * 60 * 1000);
  
  const past = [];
  const future = [];
  let current = null;
  
  sessionGroups.forEach(group => {
    if (!group.sessions || group.sessions.length === 0) {
      future.push(group);
      return;
    }
    
    // Sort sessions by date
    const sortedSessions = [...group.sessions].sort((a, b) => 
      new Date(a.scheduledAt) - new Date(b.scheduledAt)
    );
    
    // Check if last session is past
    const lastSession = sortedSessions[sortedSessions.length - 1];
    const lastSessionDate = new Date(lastSession.scheduledAt);
    
    if (lastSessionDate < sixtyMinutesAgo) {
      past.push(group);
    } else {
      // Find next upcoming session
      const nextSession = sortedSessions.find(s => new Date(s.scheduledAt) > now);
      if (nextSession) {
        group.nextSession = {
          id: nextSession.id,
          title: nextSession.title,
          scheduledAt: nextSession.scheduledAt,
          joinUrl: nextSession.joinUrl
        };
      }
      
      if (!current) {
        current = group;
      } else {
        // Compare dates to determine which is more current
        const currentNextSession = current.sessions.find(s => new Date(s.scheduledAt) > now);
        if (nextSession && currentNextSession) {
          if (new Date(nextSession.scheduledAt) < new Date(currentNextSession.scheduledAt)) {
            future.push(current);
            current = group;
          } else {
            future.push(group);
          }
        } else if (nextSession && !currentNextSession) {
          future.push(current);
          current = group;
        } else {
          future.push(group);
        }
      }
    }
  });
  
  return { past, current, future };
};

/**
 * Formats a date string into session date components
 * @param {string} dateString - ISO date string
 * @returns {Object} Formatted date components
 */
export const formatSessionDate = (dateString) => {
  if (!dateString) {
    return {
      date: "Date TBD",
      time: "Time TBD",
      timezone: "",
      dayOfWeek: "Day TBD",
      rawDate: null
    };
  }

  try {
    const date = new Date(dateString);
    const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeZoneAbbr = new Intl.DateTimeFormat('en', {
      timeZoneName: 'short',
      timeZone: timeZoneName
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || '';
    
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }),
      timezone: timeZoneAbbr,
      dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' }),
      rawDate: date
    };
  } catch {
    return {
      date: "Date unavailable",
      time: "Time unavailable", 
      timezone: "",
      dayOfWeek: "Day unavailable",
      rawDate: null
    };
  }
};

/**
 * Gets the next session date from a session group
 * @param {Object} sessionGroup - Session group with sessions
 * @returns {Object} Next session date info
 */
export const getNextSessionFromGroup = (sessionGroup) => {
  if (!sessionGroup?.sessions?.length) {
    return null;
  }

  const now = new Date();
  const upcomingSessions = sessionGroup.sessions
    .filter(s => new Date(s.scheduledAt) > now)
    .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

  return upcomingSessions[0] || null;
};

/**
 * Calculates week dates based on first session date
 * @param {Date} firstSessionDate - First session date
 * @param {number} weekNumber - Week number (1-based)
 * @returns {string} Formatted week date
 */
export const getWeekDate = (firstSessionDate, weekNumber) => {
  if (!firstSessionDate || !(firstSessionDate instanceof Date)) {
    return `Week ${weekNumber} TBD`;
  }

  try {
    const weekDate = new Date(firstSessionDate);
    weekDate.setDate(firstSessionDate.getDate() + (weekNumber - 1) * 7);
    
    return weekDate.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric' 
    });
  } catch {
    return `Week ${weekNumber} TBD`;
  }
}; 